import {
  type IAdminEscalationEmailInput,
  type IAssigneeDigestInput,
  type IAssignmentEmailInput,
  type IChampionDigestInput,
  type ICompletionEmailInput,
  type ISetupWizardEmailContent,
  type ISetupWizardSmsContent,
  type ISetupWizardStepInput,
  type IStepReminderSmsInput,
  type IStepReopenedEmailInput,
  type IWelcomeEmailInput,
} from '@principle-theorem/principle-core/interfaces';
import {
  CALENDAR_FORMAT_LOWERCASE,
  CASUAL_DATE_FORMAT,
  DATE_WITH_YEAR_SHORT,
  getNounForCount,
  getVerbForCount,
  initMoment,
  ISODateType,
  toMoment,
} from '@principle-theorem/shared';

const MAX_COMPLETED_SHOWN = 5;

export class SetupWizardEmails {
  static greeting(recipientName: string): string {
    return `Hi ${recipientName},\n\n`;
  }

  static signOff(): string {
    return `\n\nWarm Regards,\nPrinciple Team`;
  }

  static supportFooter(): string {
    return `\n\nNeed help? Reply to this email and your onboarding team will jump in.`;
  }

  static callToActionLabel(
    kind: 'welcome' | 'transactional' | 'digest'
  ): string {
    switch (kind) {
      case 'welcome':
        return 'Get started:';
      case 'transactional':
        return 'Complete your setup steps:';
      case 'digest':
        return 'View your setup progress:';
      default:
        return 'View your setup progress:';
    }
  }

  static formatDate(timestamp: ISODateType): string {
    return toMoment(timestamp).format(DATE_WITH_YEAR_SHORT);
  }

  static buildLaunchDateRiskMessage(targetLaunchDate?: ISODateType): string {
    if (!targetLaunchDate) {
      return '';
    }
    const formatted = toMoment(targetLaunchDate).format(DATE_WITH_YEAR_SHORT);
    return `Your launch on ${formatted} is at risk.`;
  }

  static buildWelcomeEmail(
    input: IWelcomeEmailInput
  ): ISetupWizardEmailContent {
    return {
      subject: `Welcome to ${input.practiceName} workspace setup`,
      body:
        SetupWizardEmails.greeting(input.recipientName) +
        SetupWizardEmails._buildWelcomeBodySection(input.practiceName) +
        SetupWizardEmails._buildCallToAction('welcome', input.wizardLink) +
        SetupWizardEmails.signOff(),
    };
  }

  static buildCompletionEmail(
    input: ICompletionEmailInput
  ): ISetupWizardEmailContent {
    return {
      subject: `🎉 ${input.practiceName} setup is complete!`,
      body:
        SetupWizardEmails.greeting(input.recipientName) +
        SetupWizardEmails._buildCompletionBodySection(input.practiceName) +
        SetupWizardEmails._buildCallToAction('digest', input.wizardLink) +
        SetupWizardEmails.supportFooter() +
        SetupWizardEmails.signOff(),
    };
  }

  static buildAdminEscalationEmail(
    input: IAdminEscalationEmailInput
  ): ISetupWizardEmailContent {
    const stepWord = getNounForCount(input.steps, 'step');
    const verbPhrase = getVerbForCount(input.steps);
    const overdueIntro = `${input.steps.length} setup ${stepWord} assigned to ${input.assigneeName} for ${input.practiceName} ${verbPhrase} overdue and need your attention.\n\n`;
    const overdueListSection = SetupWizardEmails._buildOverdueListSection(
      input.steps,
      input.targetLaunchDate
    );

    return {
      subject: `${input.steps.length} setup ${stepWord} need attention for ${input.practiceName}`,
      body:
        SetupWizardEmails.greeting(input.championName) +
        overdueIntro +
        overdueListSection +
        `Please help unblock or reassign these steps.\n\n` +
        SetupWizardEmails._buildCallToAction(
          'transactional',
          input.wizardLink
        ) +
        SetupWizardEmails.supportFooter() +
        SetupWizardEmails.signOff(),
    };
  }

  static buildAssignmentEmail(
    input: IAssignmentEmailInput
  ): ISetupWizardEmailContent {
    const stepWord = getNounForCount(input.steps, 'step');
    const intro = `You've been assigned the following setup ${stepWord} for ${input.practiceName}:\n\n`;
    const assignedListSection = SetupWizardEmails._buildAssignedListSection(
      input.steps,
      input.targetLaunchDate
    );

    return {
      subject: `You've been assigned ${input.steps.length} setup ${stepWord} for ${input.practiceName}`,
      body:
        SetupWizardEmails.greeting(input.recipientName) +
        intro +
        assignedListSection +
        SetupWizardEmails._buildCallToAction(
          'transactional',
          input.wizardLink
        ) +
        SetupWizardEmails.signOff(),
    };
  }

  static buildStepReminderEmail(
    input: IAssignmentEmailInput
  ): ISetupWizardEmailContent {
    const stepWord = getNounForCount(input.steps, 'step');
    const intro = `The following setup ${stepWord} for ${input.practiceName} are overdue:\n\n`;
    const overdueListSection = SetupWizardEmails._buildOverdueListSection(
      input.steps,
      input.targetLaunchDate
    );

    return {
      subject: `${input.steps.length} setup ${stepWord} overdue for ${input.practiceName}`,
      body:
        SetupWizardEmails.greeting(input.recipientName) +
        intro +
        overdueListSection +
        `If you need more time or run into trouble, please reach out to your account manager, or reply to this email.\n\n` +
        SetupWizardEmails._buildCallToAction(
          'transactional',
          input.wizardLink
        ) +
        SetupWizardEmails.signOff(),
    };
  }

  static buildStepReminderSms(
    input: IStepReminderSmsInput
  ): ISetupWizardSmsContent {
    const stepWord = getNounForCount(input.stepCount, 'step');
    return {
      message: `${input.stepCount} setup ${stepWord} overdue for ${input.practiceName}. Complete at: ${input.wizardLink}`,
    };
  }

  static buildStepReopenedEmail(
    input: IStepReopenedEmailInput
  ): ISetupWizardEmailContent {
    const callToActionLabel =
      SetupWizardEmails.callToActionLabel('transactional');
    return {
      subject: `Setup step reopened: ${input.stepTitle}`,
      body:
        SetupWizardEmails.greeting(input.recipientName) +
        `The step "${input.stepTitle}" for ${input.practiceName} has been reopened and needs your attention.\n\n` +
        `${callToActionLabel} ${input.wizardLink}` +
        SetupWizardEmails.signOff(),
    };
  }

  static buildChampionDigestEmail(
    input: IChampionDigestInput
  ): ISetupWizardEmailContent {
    const lines: string[] = [`Hi ${input.recipientName},`, ''];

    SetupWizardEmails._appendChampionProgressSection(lines, input);
    SetupWizardEmails._appendChampionCompletedSection(lines, input);
    SetupWizardEmails._appendChampionOverdueSection(lines, input);
    SetupWizardEmails._appendChampionDueThisWeekSection(lines, input);
    SetupWizardEmails._appendChampionComingUpSection(lines, input);
    SetupWizardEmails._appendChampionUnscheduledSection(lines, input);

    lines.push(
      `${SetupWizardEmails.callToActionLabel('digest')} ${input.wizardLink}`
    );
    lines.push(SetupWizardEmails.signOff());

    return {
      subject: `${input.practiceName} Setup - ${input.completionPercentage}% complete (${input.completedCount}/${input.totalSteps} steps)`,
      body: lines.join('\n'),
    };
  }

  static buildAssigneeDigestEmail(
    input: IAssigneeDigestInput
  ): ISetupWizardEmailContent {
    const lines: string[] = [];

    SetupWizardEmails._appendAssigneeIntroSection(lines, input);
    SetupWizardEmails._appendAssigneeOpenStepsSection(lines, input);
    SetupWizardEmails._appendAssigneeOverdueNoteSection(lines, input);
    SetupWizardEmails._appendAssigneeCallToActionSection(lines, input);

    return {
      subject: `Your Setup Steps: ${input.practiceName} - ${input.openSteps.length} open this week`,
      body: lines.join('\n'),
    };
  }

  private static _appendAssigneeIntroSection(
    lines: string[],
    input: IAssigneeDigestInput
  ): void {
    const stepWord = getNounForCount(input.openSteps, 'step');
    lines.push(`Hi ${input.recipientName},`);
    lines.push('');
    lines.push(
      `You have ${input.openSteps.length} ${stepWord} remaining for ${input.practiceName}.`
    );
    lines.push('');
  }

  private static _appendAssigneeOpenStepsSection(
    lines: string[],
    input: IAssigneeDigestInput
  ): void {
    if (input.openSteps.length === 0) {
      return;
    }
    lines.push('Your open steps:');
    for (const step of input.openSteps) {
      const suffix = step.dueDate
        ? ` - due ${SetupWizardEmails.formatDate(step.dueDate)}`
        : '';
      lines.push(`  - ${step.title}${suffix}`);
    }
    lines.push('');
  }

  private static _appendAssigneeOverdueNoteSection(
    lines: string[],
    input: IAssigneeDigestInput
  ): void {
    if (input.overdueCount <= 0) {
      return;
    }
    const overdueStepWord = getNounForCount(input.overdueCount, 'step');
    const stepVerb = getVerbForCount(input.overdueCount);
    lines.push(
      `Note: ${input.overdueCount} of your ${overdueStepWord} ${stepVerb} overdue.`
    );
    lines.push('');
  }

  private static _appendAssigneeCallToActionSection(
    lines: string[],
    input: IAssigneeDigestInput
  ): void {
    const label = SetupWizardEmails.callToActionLabel('digest');
    lines.push(`${label} ${input.wizardLink}`);
    lines.push(SetupWizardEmails.signOff());
  }

  private static _buildWelcomeBodySection(practiceName: string): string {
    return (
      `Welcome aboard!\n\n` +
      `You've been added to the setup team for ${practiceName}. Our setup wizard walks you through the steps to get your practice live on Principle. Most teams finish in 2–3 weeks.\n\n` +
      `What to expect:\n` +
      ` - A guided checklist of setup steps\n` +
      ` - Email reminders when steps are due\n` +
      ` - A single place to track your progress\n\n`
    );
  }

  private static _buildCompletionBodySection(practiceName: string): string {
    return (
      `Great news! The setup wizard for ${practiceName} is now complete. All steps have been finished and your practice is ready for the next phase.\n\n` +
      `What happens next:\n` +
      ` - Our team will begin the initial data migration\n` +
      ` - You'll be notified when your practice is ready to go live\n` +
      ` - In the meantime, you can review your setup at the link below\n\n`
    );
  }

  private static _buildCallToAction(
    kind: 'welcome' | 'transactional' | 'digest',
    wizardLink: string
  ): string {
    return `${SetupWizardEmails.callToActionLabel(kind)} ${wizardLink}`;
  }

  private static _buildOverdueListSection(
    steps: ISetupWizardStepInput[],
    targetLaunchDate?: ISODateType
  ): string {
    const stepList = SetupWizardEmails._buildOverdueStepList(steps);
    const launchMessage =
      SetupWizardEmails._buildSoftLaunchMessage(targetLaunchDate);
    const launchRiskSection = launchMessage ? `\n\n${launchMessage}` : '';
    return `${stepList}${launchRiskSection}\n\n`;
  }

  private static _buildAssignedListSection(
    steps: ISetupWizardStepInput[],
    targetLaunchDate?: ISODateType
  ): string {
    const stepList = SetupWizardEmails._buildStepList(steps);
    const launchMessage =
      SetupWizardEmails._buildInfoLaunchMessage(targetLaunchDate);
    const launchRiskSection = launchMessage ? `\n\n${launchMessage}` : '';
    return `${stepList}${launchRiskSection}\n\n`;
  }

  private static _appendChampionProgressSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    lines.push('📊 PROGRESS');
    if (input.targetLaunchDate) {
      const launchDate = toMoment(input.targetLaunchDate);
      const daysRemaining = launchDate.diff(input.now, 'days');
      const launchInfo = ` - ${daysRemaining} days to launch (${launchDate.format(
        CASUAL_DATE_FORMAT
      )})`;
      lines.push(
        `${input.completedCount} of ${input.totalSteps} steps complete (${input.completionPercentage}%)${launchInfo}`
      );
    } else {
      lines.push(
        `${input.completedCount} of ${input.totalSteps} steps complete (${input.completionPercentage}%)`
      );
    }
    lines.push('');
  }

  private static _appendChampionCompletedSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    if (input.completedThisWeek.length === 0) {
      return;
    }
    lines.push('✅ RECENTLY COMPLETED');
    const shown = input.completedThisWeek.slice(0, MAX_COMPLETED_SHOWN);
    for (const step of shown) {
      const day = step.completedAt
        ? toMoment(step.completedAt).calendar(CALENDAR_FORMAT_LOWERCASE)
        : '';
      const assignee = SetupWizardEmails._formatAssignee(step);
      lines.push(`  - ${step.title} - completed ${day}${assignee}`);
    }
    const remaining = input.completedThisWeek.length - MAX_COMPLETED_SHOWN;
    if (remaining > 0) {
      lines.push(`  ... and ${remaining} more completed this week`);
    }
    lines.push('');
  }

  private static _appendChampionOverdueSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    if (input.overdueSteps.length === 0) {
      return;
    }
    lines.push('🔴 NEEDS ATTENTION');
    const sorted = [...input.overdueSteps].sort((stepA, stepB) => {
      const aDays = stepA.dueDate
        ? toMoment(input.now).diff(toMoment(stepA.dueDate), 'days')
        : 0;
      const bDays = stepB.dueDate
        ? toMoment(input.now).diff(toMoment(stepB.dueDate), 'days')
        : 0;
      return bDays - aDays;
    });
    for (const step of sorted) {
      const days = step.dueDate
        ? toMoment(input.now).diff(toMoment(step.dueDate), 'days')
        : 0;
      const dayWord = getNounForCount(days, 'day');
      const assignee = SetupWizardEmails._formatAssignee(step);
      lines.push(
        `  - ${step.title} - overdue by ${days} ${dayWord}${assignee}`
      );
    }
    lines.push('');
  }

  private static _appendChampionDueThisWeekSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    lines.push('📅 DUE THIS WEEK');
    if (input.dueThisWeek.length > 0) {
      const sorted = [...input.dueThisWeek].sort(
        (stepA, stepB) =>
          (stepA.dueDate ? toMoment(stepA.dueDate).unix() : 0) -
          (stepB.dueDate ? toMoment(stepB.dueDate).unix() : 0)
      );
      for (const step of sorted) {
        const due = step.dueDate
          ? toMoment(step.dueDate).format(CASUAL_DATE_FORMAT)
          : '';
        const assignee = SetupWizardEmails._formatAssignee(step);
        lines.push(`  - ${step.title} - due ${due}${assignee}`);
      }
    } else {
      lines.push('  Nothing due this week 🎉');
    }
    lines.push('');
  }

  private static _appendChampionComingUpSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    if (input.comingUpSteps.length === 0) {
      return;
    }
    lines.push('🔜 COMING UP');
    const sorted = [...input.comingUpSteps].sort(
      (stepA, stepB) =>
        (stepA.dueDate ? toMoment(stepA.dueDate).unix() : 0) -
        (stepB.dueDate ? toMoment(stepB.dueDate).unix() : 0)
    );
    for (const step of sorted) {
      const due = step.dueDate
        ? toMoment(step.dueDate).format(CASUAL_DATE_FORMAT)
        : '';
      const assignee = SetupWizardEmails._formatAssignee(step);
      lines.push(`  - ${step.title} - due ${due}${assignee}`);
    }
    lines.push('');
  }

  private static _appendChampionUnscheduledSection(
    lines: string[],
    input: IChampionDigestInput
  ): void {
    if (input.unscheduledSteps.length === 0) {
      return;
    }
    lines.push('📋 NOT YET SCHEDULED');
    for (const step of input.unscheduledSteps) {
      const assignee = SetupWizardEmails._formatAssignee(step);
      lines.push(`  - ${step.title}${assignee}`);
    }
    lines.push('');
  }

  private static _buildInfoLaunchMessage(
    targetLaunchDate?: ISODateType
  ): string {
    if (!targetLaunchDate) {
      return '';
    }
    const launchDate = SetupWizardEmails.formatDate(targetLaunchDate);
    return `Your target launch date is ${launchDate}.`;
  }

  private static _buildSoftLaunchMessage(
    targetLaunchDate?: ISODateType
  ): string {
    if (!targetLaunchDate) {
      return '';
    }
    const launchDate = SetupWizardEmails.formatDate(targetLaunchDate);
    return `Your launch on ${launchDate} may be affected.`;
  }

  private static _buildStepList(steps: ISetupWizardStepInput[]): string {
    return steps
      .map((step) => {
        if (!step.dueDate) {
          return `- ${step.title}`;
        }
        const dueDate = SetupWizardEmails.formatDate(step.dueDate);
        const due = ` (due ${dueDate})`;
        return `- ${step.title}${due}`;
      })
      .join('\n');
  }

  private static _buildOverdueStepList(steps: ISetupWizardStepInput[]): string {
    const now = initMoment();
    return [...steps]
      .sort((stepA, stepB) => {
        const aDays = stepA.dueDate
          ? now.diff(toMoment(stepA.dueDate), 'days')
          : 0;
        const bDays = stepB.dueDate
          ? now.diff(toMoment(stepB.dueDate), 'days')
          : 0;
        return bDays - aDays;
      })
      .map((step) => {
        if (!step.dueDate) {
          return `- ${step.title}`;
        }
        const daysOverdue = now.diff(toMoment(step.dueDate), 'days');
        if (daysOverdue <= 0) {
          const dueDate = SetupWizardEmails.formatDate(step.dueDate);
          return `- ${step.title} — due ${dueDate}`;
        }
        const dayWord = daysOverdue === 1 ? 'day' : 'days';
        return `- ${step.title} — ${daysOverdue} ${dayWord} overdue`;
      })
      .join('\n');
  }

  private static _formatAssignee(step: { assigneeName?: string }): string {
    return step.assigneeName ? ` (${step.assigneeName})` : '';
  }
}
